import React, {useEffect, useState} from 'react';
import {css, styled} from 'styled-components';
import DefaultImageView from '../components/common/DefaultImageView';
import DefaultButton from '../components/common/DefaultButton';
import {colors} from '../styles/colors';
import {useNavigate} from 'react-router-dom';
import {getDevice} from '../utils/getDevice';
import ScreenLayout from '../components/common/ScreenLayout';
import {useRecoilState} from 'recoil';
import {baseUrlTypeState, IBaseUrlType, userInfoState} from '../utils/Atoms';
import {countryArray} from 'data/country';
import CheckBox from 'components/login/CheckBox';
import Footer from 'components/common/Footer';
import PhoneNumberInput from 'components/common/PhoneNumberInput';
import LabelInput from 'components/common/LabelInput';
import {doLogin} from 'function/converter';
import {isSignInValidate, isValidatePhoneNumber} from 'function/validate';
import {useFormatMsgArray} from 'utils/useFormatMsg';
import {minmax} from 'utils/minmax';
import CommonModal from 'components/common/CommonModal';
import {IModalState} from './Register';

/**
 * 로그인 페이지 컴포넌트
 * 사용자 인증 및 로그인 처리를 담당합니다.
 * @returns {JSX.Element} 로그인 페이지 컴포넌트
 */
const Login = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState({});
  // ID, Password State
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [isValidateStyle, setIsValidateStyle] = useState(false);
  const [selectCountry, setSelectCountry] = useState(countryArray[0]);

  // autoLogin CheckBox State
  const [autoLogin, setAutoLogin] = useState<boolean>(false);
  // Visible Password State
  const [isVisiblePassword, setIsVisiblePassword] = useState<boolean>(false);

  // const [baseUrlType, setBaseUrlType] = useState('dev');
  const type: IBaseUrlType =
    process.env.REACT_APP_NODE_ENV === 'production' ? 'prod' : 'dev';
  const [baseUrlType, setBaseUrlType] = useRecoilState(baseUrlTypeState);

  const [modal, setModal] = useState('');

  const navigate = useNavigate();
  const device = getDevice();
  const [_userInfo, setUserInfo] = useRecoilState(userInfoState);

  const textArray = [
    'enterID',
    'enterPassword',
    'login',
    'searchPassword',
    'copyright',
    'autoLogin',
    'loginTitle1',
    'loginTilePrimary',
    'loginTitle2',
    'loginTitle3',
    'loginDetail1',
    'loginDetail2',
    'loginDetail3',
    'id',
    'phoneNumberId',
  ];
  const text = useFormatMsgArray(textArray);

  useEffect(() => {
    const checkValidate = async () => {
      const errorArray = await isSignInValidate(id, password);
      const phoneValidate = isValidatePhoneNumber(
        id,
        selectCountry.languageCode,
      );

      if (phoneValidate && errorArray.length === 0) {
        setIsValidateStyle(true);
      } else {
        setIsValidateStyle(false);
      }
    };
    checkValidate();
  }, [id, password]);

  /**
   * 로그인 처리 함수
   * 유효성 검사를 통과한 경우 로그인 API를 호출하고 성공 시 메인 페이지로 이동합니다.
   * @returns {Promise<void>}
   */
  const handleLogin = async () => {
    if (isValidateStyle) {
      const result = await doLogin({
        setIsLoading,
        password,
        id,
        setUserInfo,
        autoLogin,
        setErrorModal,
        setModalState,
      });

      if (result) {
        setBaseUrlType(type);
        navigate('/main');
      }
    }
  };

  /**
   * 키보드 입력 이벤트 핸들러
   * Enter 키 입력 시 로그인을 처리합니다.
   * @param {any} e - 키보드 이벤트 객체
   * @returns {Promise<void>}
   */
  const handleOnKeyPress = async (e: any) => {
    if (e.key === 'Enter') {
      await handleLogin();
    }
  };

  // 모달
  const initModalState: IModalState = {
    tempInfo: {state: '', open: false, info: {}},
  };
  const [modalState, setModalState] = useState<IModalState>(initModalState);

  const modalData: {[key: string]: any} = {
    recover: {
      title: 'recoverModalTitle',
      detail: 'recoverModalDetail',
      firstButtonText: 'cancel',
      secondButtonText: 'confirm',
      firstButtonStyle: {
        backgroundColor: colors.borderGray,
      },
      secondButtonStyle: {
        backgroundColor: colors.primary2,
      },
      firstButtonClick: () => {
        setModal('');
      },
      secondButtonClick: async () => {
        //
      },
    },
    completeRecover: {
      title: 'completeRecoverTitle',
      detail: 'completeRecoverDetail',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
        window.location.reload();
      },
    },
  };

  return (
    <ScreenLayout isLoading={isLoading} noHeader={true} errorModal={errorModal}>
      <Container>
        <Main $device={device}>
          {/* 설명 */}
          <ExplainContainer $device={device}>
            <ExplainTtileContainer>
              <ExplainTitle style={{marginBottom: 'max(10px, min(1vw, 20px))'}}>
                {/* {text.loginTitle1} */}
              </ExplainTitle>
              <ExplainTitle $device={device}>
                <ExplainTitleBackground>
                  {text.loginTilePrimary}
                </ExplainTitleBackground>
                {text.loginTitle2}
              </ExplainTitle>
              <ExplainTitle $device={device}>{text.loginTitle3}</ExplainTitle>
            </ExplainTtileContainer>
            <ExplainDetailContainer>
              <ExplainDetail style={{marginBottom: 'max(5px, min(2vw, 10px))'}}>
                <ExplainDetailSpan>{text.loginDetail1}</ExplainDetailSpan>
                {text.loginDetail2}
              </ExplainDetail>
              <ExplainDetail>{text.loginDetail3}</ExplainDetail>
            </ExplainDetailContainer>

            <DefaultImageView
              source="images/login/login_image.png"
              style={{width: '100%', marginTop: 'max(20px, min(2vw, 40px))'}}
            />
          </ExplainContainer>

          <LoginContainer $device={device}>
            <InnerContainer $device={device}>
              {/* 로고 이미지 */}
              <LogoContainer>
                <DefaultImageView
                  source="images/login/login_logo.png"
                  style={{
                    width: '65%',
                  }}
                />
              </LogoContainer>
              <Wrapper>
                <InputWrapper>
                  <InputContainer>
                    {/* baseurl 선택 */}
                    {/* <LabelRadio
                      isColumn
                      labelText="BASE URL 선택"
                      value={baseUrlType}
                      options={[
                        {
                          text: 'Development',
                          value: 'dev',
                        },
                        {
                          text: 'Production',
                          value: 'prod',
                        },
                      ]}
                      setValue={() => {
                        setBaseUrlType(type);
                        window.location.reload();
                      }}
                    /> */}
                    {/* ID 입력란 */}
                    <PhoneNumberInput
                      label="phoneNumberId"
                      selectCountry={selectCountry}
                      setSelectCountry={setSelectCountry}
                      phoneNumber={id}
                      setPhoneNumber={setId}
                    />
                    {/* 비밀번호 */}
                    <LabelInput
                      labelText="enterPassword"
                      type="password"
                      value={password}
                      setValue={setPassword}
                      isVisiblePassword={isVisiblePassword}
                      setVisiblePassword={setIsVisiblePassword}
                      onKeyPress={(e: any) => handleOnKeyPress(e)}
                      isColumn
                    />
                  </InputContainer>
                </InputWrapper>
                {/* 자동 로그인 */}
                <RadioContainer>
                  <CheckBox
                    style={{display: 'none'}}
                    checked={autoLogin}
                    onChange={() => {
                      setAutoLogin(!autoLogin);
                      window.location.reload();
                    }}
                    checkOnImg="images/login/check_on.png"
                    checkOffImg="images/login/check_off.png">
                    <RadioText>{text.autoLogin}</RadioText>
                  </CheckBox>
                </RadioContainer>
                {/* 로그인 버튼 */}
                <DefaultButton
                  text="login"
                  buttonStyle={{
                    width: '100%',
                    height: 'max(40px, min(3.8vw, 60px))',
                    marginBottom: 'max(20px, min(2vw, 40px))',
                    backgroundColor: isValidateStyle
                      ? colors.primary2
                      : '#ededed',
                  }}
                  textStyle={{
                    color: isValidateStyle ? colors.white : colors.borderGray,
                    fontSize: 'max(18px, min(1.5vw, 24px))',
                  }}
                  onClick={async () => {
                    await handleLogin();
                  }}
                />
              </Wrapper>
            </InnerContainer>
            <InnerBottomBtns $device={device}>
              {/* 회원가입 */}
              {/* <DefaultText
                text="joinMembership"
                textStyle={{
                  fontSize: 'max(12px, min(1vw, 20px))',
                  color: colors.gray,
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/register')}
              /> */}
              {/* 비밀번호 찾기 */}
              {/* <DefaultText
                text="searchPassword"
                textStyle={{
                  fontSize: 'max(12px, min(1vw, 20px))',
                  color: colors.gray,
                  cursor: 'pointer',
                }}
                onClick={() =>
                  navigate('/resetPassword', {
                    state: {
                      type: 'searchPassword',
                    },
                  })
                }
              /> */}
            </InnerBottomBtns>
          </LoginContainer>
        </Main>
        <Footer isNarrow />
      </Container>

      {modalState.tempInfo.open && (
        <CommonModal
          title={'tempInfoModalTitle'}
          detail={'tempInfoModalDetail'}
          firstButtonText={'confirm'}
          firstButtonClick={() => {
            const info = modalState.tempInfo.info;
            const state = modalState.tempInfo.state;
            if (state === 'NEED_PASSWORD_SET') {
              navigate('/register', {state: {step: 1, info}});
            } else if (state === 'ACCOUNT_INITIALIZED') {
              navigate('/register', {state: {step: 2, info}});
            } else if (state === 'PROFILE_SETTING_INITIALIZED') {
              navigate('/register', {state: {step: 3, info}});
            } else if (state === 'TERMS_OF_SERVICE_AGREEMENT') {
              navigate('/register', {state: {step: 4, info}});
            }
            setModalState(initModalState);
          }}
        />
      )}

      {modal !== '' && (
        <CommonModal
          title={modalData[modal].title}
          detail={modalData[modal].detail}
          firstButtonText={modalData[modal].firstButtonText}
          firstButtonStyle={modalData[modal].firstButtonStyle}
          secondButtonText={modalData[modal].secondButtonText}
          secondButtonStyle={modalData[modal].secondButtonStyle}
          firstButtonClick={modalData[modal].firstButtonClick}
          secondButtonClick={modalData[modal].secondButtonClick}
          closeModal={() => setModal('')}
        />
      )}
    </ScreenLayout>
  );
};

export default Login;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const Main = styled.div<{$device?: string}>`
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex: 1;
  flex-shrink: 0;
  ${(props: any) =>
    props.$device !== 'pc' &&
    css`
      flex-direction: column;
      flex: none;
    `}
`;

const LoginContainer = styled.div<{$device?: string}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 40%;
  ${(props: any) =>
    props.$device !== 'pc' &&
    css`
      width: 100%;
    `}
`;

const LogoContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-bottom: max(34px, min(3.5vw, 68px));
`;

const Wrapper = styled.div`
  width: 90%;
`;

const InnerContainer = styled.div<{$device?: string}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 60%;
  flex-direction: column;
  border-radius: ${minmax(10)};
  border: solid 1px ${colors.borderGray};
  padding-top: max(30px, min(3vw, 60px));
  margin-bottom: max(14px, min(2vw, 28px));
  ${(props: any) =>
    props.$device !== 'pc' &&
    css`
      width: 80%;
      height: 100%;
      margin-top: 10vh;
    `}
`;

const ExplainContainer = styled.div<{$device?: string}>`
  height: 100%;
  width: 40vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props: any) =>
    props.$device !== 'pc' &&
    css`
      width: 80%;
    `}
`;
const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  display: flex;
  /* height: 176px; */
  flex-direction: column;
  justify-content: space-between;
  row-gap: max(14px, min(1.5vw, 28px));
`;
const InnerBottomBtns = styled.div<{$device?: string}>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${(props: any) =>
    props.$device !== 'pc' &&
    css`
      width: 80%;
    `}
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: max(12px, min(1.2vw, 24px));
  margin-bottom: max(24px, min(2.5vw, 48px));
`;

const RadioText = styled.p`
  height: 100%;
  display: flex;
  align-items: center;
  font-weight: 400;
  letter-spacing: -0.72px;
  color: ${colors.black};
  font-size: ${minmax(20)};
  margin-left: 8px;
`;

const ExplainTtileContainer = styled.div`
  margin-bottom: max(10px, min(1.5vw, 30px));
`;

const ExplainTitle = styled.p<{$device?: string}>`
  font-size: max(20px, min(1.8vw, 36px));
  font-weight: 500;
  line-height: 1.67;
  letter-spacing: -1.08px;
  color: ${colors.black};
  ${(props: any) =>
    props.$device !== 'pc' &&
    css`
      line-height: 130%;
    `}
`;

const ExplainTitleBackground = styled.span`
  font-size: max(20px, min(1.8vw, 36px));
  font-weight: 500;
  /* letter-spacing: -1px; */
  color: ${colors.white};
  background-color: ${colors.primary};
  padding-left: 5px;
  padding-right: 5px;
`;

const ExplainDetail = styled.p`
  font-size: max(10px, min(1.5vw, 20px));
  letter-spacing: -0.6px;
  color: ${colors.gray};
`;

const ExplainDetailSpan = styled.span`
  font-size: max(10px, min(1.5vw, 20px));
  letter-spacing: -0.6px;
  color: ${colors.gray};
  font-weight: 500;
`;

const ExplainDetailContainer = styled.div`
  margin-bottom: max(10px, min(1.5vw, 30px));
`;
